import { el, clear, onTeardown } from '../lib/dom.js';
import { loadSnapshot } from '../data/snapshot.js';
import { resolvePokedex, dexRows, prettify } from '../data/pokedex-resolver.js';
import { settings } from '../store/settings.js';
import { ui } from '../store/ui.js';
import { selection, stats, entry, gameEntry } from '../store/selection.js';
import { Card } from '../components/Card.js';
import { ProgressRing } from '../components/ProgressRing.js';
import { Sprite } from '../components/Sprite.js';
import { typesForGen } from '../lib/type-resolve.js';
import { onTapCode } from '../lib/tap-code.js';

const TYPES = [
  'normal',
  'fire',
  'water',
  'electric',
  'grass',
  'ice',
  'fighting',
  'poison',
  'ground',
  'flying',
  'psychic',
  'bug',
  'rock',
  'ghost',
  'dragon',
  'dark',
  'steel',
  'fairy',
];

const FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'caught', label: 'Caught', test: (e) => e.caught },
  { key: 'missing', label: 'Missing', test: (e) => !e.caught },
  { key: 'favorite', label: 'Favorites', test: (e) => e.favorite },
  { key: 'legendary', label: 'Legendary', test: (e, sp) => sp.isLegendary || sp.isMythical },
  { key: 'notes', label: 'Notes', test: (e) => !!(e.note && e.note.trim()) },
];

const SORTS = [
  { key: 'dex', label: 'Dex number', desc: false },
  { key: 'name', label: 'Name (A–Z)', desc: false },
  { key: 'bst', label: 'Base stat total', desc: true },
  { key: 'hp', label: 'HP', desc: true },
  { key: 'atk', label: 'Attack', desc: true },
  { key: 'def', label: 'Defense', desc: true },
  { key: 'spa', label: 'Sp. Attack', desc: true },
  { key: 'spd', label: 'Sp. Defense', desc: true },
  { key: 'spe', label: 'Speed', desc: true },
  { key: 'height', label: 'Height', desc: true },
  { key: 'weight', label: 'Weight', desc: true },
  { key: 'baseExp', label: 'Base EXP', desc: true },
  { key: 'catchrate', label: 'Catch rate', desc: true },
  { key: 'caught', label: 'Recently caught', desc: true },
];
const STAT_SHORT = { hp: 'HP', atk: 'Atk', def: 'Def', spa: 'SpA', spd: 'SpD', spe: 'Spe' };

const SEARCH_ICON =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>';

function sortValue(row, key) {
  const sp = row.species;
  switch (key) {
    case 'dex':
      return row.number ?? sp.id;
    case 'name':
      return sp.name;
    case 'bst':
      return sp.bst ?? 0;
    case 'hp':
    case 'atk':
    case 'def':
    case 'spa':
    case 'spd':
    case 'spe':
      return sp.stats?.[key] ?? 0;
    case 'height':
      return sp.height ?? 0;
    case 'weight':
      return sp.weight ?? 0;
    case 'baseExp':
      return sp.baseExp ?? 0;
    case 'catchrate':
      return sp.captureRate ?? 0;
    case 'caught':
      return gameEntry(sp.id).updatedAt || '';
    default:
      return 0;
  }
}

function metricLabel(key, sp) {
  switch (key) {
    case 'bst':
      return `BST ${sp.bst}`;
    case 'hp':
    case 'atk':
    case 'def':
    case 'spa':
    case 'spd':
    case 'spe':
      return `${STAT_SHORT[key]} ${sp.stats?.[key] ?? 0}`;
    case 'height':
      return `${((sp.height ?? 0) / 10).toFixed(1)} m`;
    case 'weight':
      return `${((sp.weight ?? 0) / 10).toFixed(1)} kg`;
    case 'baseExp':
      return sp.baseExp != null ? `${sp.baseExp} EXP` : null;
    case 'catchrate':
      return `Catch ${sp.captureRate ?? '?'}`;
    default:
      return null;
  }
}

export async function DexGrid() {
  const view = el('section', { class: 'view dexgrid' });
  const snap = await loadSnapshot();

  const grid = el('div', { class: 'grid' });
  let query = '';
  let filterKey = ui.get().filter || 'all';
  let filterType = ui.get().filterType || '';
  let filterGen = Number(ui.get().filterGen) || 0;
  let filterAbility = Number(ui.get().filterAbility) || 0;
  let filterEggGroup = ui.get().filterEggGroup || '';
  let filterColor = ui.get().filterColor || '';
  let filterMinBst = Number(ui.get().filterMinBst) || 0;
  let filterFullyEvolved = !!ui.get().filterFullyEvolved;
  let sortKey = ui.get().sort || 'dex';
  let sortDesc = !!ui.get().sortDesc;
  let rows = [];
  let ids = [];
  let lastList = [];

  const ring = ProgressRing();

  const title = el('h1', {});
  // Which game is active — a quiet label, not a control. Change it from the
  // Games button in the nav.
  const gameLabel = el('span', { class: 'feed-head__game' });

  const search = el('input', {
    class: 'field-search__input',
    type: 'search',
    placeholder: 'Filter this Pokédex…',
    oninput: (e) => {
      query = e.target.value.trim().toLowerCase();
      paintGrid();
    },
  });

  const filterBar = el('div', { class: 'feed-chips' });
  const chipEls = FILTERS.map((f) => {
    const count = el('span', { class: 'feed-chip__count' });
    const btn = el(
      'button',
      {
        class: `feed-chip${f.key === filterKey ? ' is-active' : ''}`,
        type: 'button',
        onclick: () => {
          filterKey = f.key;
          ui.set({ filter: f.key });
          chipEls.forEach((c, i) => c.btn.classList.toggle('is-active', FILTERS[i].key === f.key));
          paintGrid();
          resetScroll();
          syncToolsBadge();
        },
      },
      el('span', {}, f.label),
      count,
    );
    filterBar.append(btn);
    return { btn, count };
  });

  const sortSelect = el(
    'select',
    {
      class: 'feed-sort__select',
      onchange: (e) => {
        sortKey = e.target.value;
        sortDesc = SORTS.find((s) => s.key === sortKey).desc;
        ui.set({ sort: sortKey, sortDesc });
        syncDirBtn();
        paintGrid();
        resetScroll();
      },
    },
    ...SORTS.map((s) => el('option', { value: s.key, selected: s.key === sortKey }, s.label)),
  );
  const dirBtn = el('button', {
    class: 'feed-sort__dir',
    type: 'button',
    title: 'Reverse order',
    onclick: () => {
      sortDesc = !sortDesc;
      ui.set({ sortDesc });
      syncDirBtn();
      paintGrid();
      resetScroll();
    },
  });
  function syncDirBtn() {
    dirBtn.textContent = sortDesc ? '↓' : '↑';
    dirBtn.setAttribute('aria-label', sortDesc ? 'Descending' : 'Ascending');
  }
  syncDirBtn();

  const typeSelect = el(
    'select',
    {
      class: 'feed-sort__select',
      onchange: (e) => {
        filterType = e.target.value;
        ui.set({ filterType });
        paintGrid();
        resetScroll();
        syncToolsBadge();
      },
    },
    el('option', { value: '' }, 'Any type'),
    ...TYPES.map((t) => el('option', { value: t, selected: t === filterType }, prettify(t))),
  );
  const genSelect = el(
    'select',
    {
      class: 'feed-sort__select',
      onchange: (e) => {
        filterGen = Number(e.target.value);
        ui.set({ filterGen });
        paintGrid();
        resetScroll();
        syncToolsBadge();
      },
    },
    el('option', { value: '0' }, 'Any gen'),
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map((g) =>
      el('option', { value: String(g), selected: g === filterGen }, `Gen ${g}`),
    ),
  );
  const randomBtn = el(
    'button',
    {
      class: 'feed-sort__dir',
      type: 'button',
      title: 'Random Pokémon',
      onclick: () => {
        if (lastList.length) {
          const pick = lastList[Math.floor(Math.random() * lastList.length)];
          location.hash = `#/pokemon/${pick.species.id}`;
        }
      },
    },
    '🎲',
  );

  // "Fully evolved" = no other species lists this one as evolvesFromId —
  // derived client-side rather than fetching every evolution chain.
  const hasNextEvo = new Set(snap.species.map((s) => s.evolvesFromId).filter(Boolean));
  const isFullyEvolved = (sp) => !hasNextEvo.has(sp.id);

  const eggGroupList = [...new Set(snap.species.flatMap((s) => s.eggGroups || []))].sort();
  const colorList = [...new Set(snap.species.map((s) => s.color).filter(Boolean))].sort();
  const abilityList = snap.abilities.slice().sort((a, b) => a.name.localeCompare(b.name));

  const abilitySelect = el(
    'select',
    {
      class: 'feed-sort__select',
      onchange: (e) => {
        filterAbility = Number(e.target.value) || 0;
        ui.set({ filterAbility });
        paintGrid();
        resetScroll();
        syncToolsBadge();
      },
    },
    el('option', { value: '0' }, 'Any ability'),
    ...abilityList.map((a) =>
      el('option', { value: String(a.id), selected: a.id === filterAbility }, prettify(a.name)),
    ),
  );

  const eggGroupSelect = el(
    'select',
    {
      class: 'feed-sort__select',
      onchange: (e) => {
        filterEggGroup = e.target.value;
        ui.set({ filterEggGroup });
        paintGrid();
        resetScroll();
        syncToolsBadge();
      },
    },
    el('option', { value: '' }, 'Any egg group'),
    ...eggGroupList.map((g) =>
      el('option', { value: g, selected: g === filterEggGroup }, prettify(g)),
    ),
  );

  const colorSelect = el(
    'select',
    {
      class: 'feed-sort__select',
      onchange: (e) => {
        filterColor = e.target.value;
        ui.set({ filterColor });
        paintGrid();
        resetScroll();
        syncToolsBadge();
      },
    },
    el('option', { value: '' }, 'Any color'),
    ...colorList.map((c) => el('option', { value: c, selected: c === filterColor }, prettify(c))),
  );

  const minBstField = el(
    'label',
    { class: 'feed-minbst' },
    el('span', {}, 'Min BST'),
    el('input', {
      type: 'number',
      min: '0',
      max: '1000',
      step: '10',
      placeholder: '0',
      value: filterMinBst || '',
      onchange: (e) => {
        filterMinBst = Math.max(0, Number(e.target.value) || 0);
        e.target.value = filterMinBst || '';
        ui.set({ filterMinBst });
        paintGrid();
        resetScroll();
        syncToolsBadge();
      },
    }),
  );

  const fullyEvolvedCheck = el(
    'label',
    { class: 'lookup__check' },
    el('input', {
      type: 'checkbox',
      checked: filterFullyEvolved,
      onchange: (e) => {
        filterFullyEvolved = e.target.checked;
        ui.set({ filterFullyEvolved });
        paintGrid();
        resetScroll();
        syncToolsBadge();
      },
    }),
    'Fully evolved only',
  );

  // Changing the list order/contents makes the old scroll offset meaningless.
  function resetScroll() {
    ui.set({ feedScroll: 0 });
    window.scrollTo(0, 0);
  }

  // Return to the saved scroll offset. Retry a few times — the grid's height
  // isn't final until the webfont loads and off-screen cards settle — but
  // stop once we've reached the target or the user starts scrolling.
  function restoreScroll() {
    const y = ui.get().feedScroll || 0;
    if (!y) return;
    let applied = null;
    const go = () => {
      if (applied != null && Math.abs(window.scrollY - applied) > 8) return; // user moved
      window.scrollTo(0, y);
      applied = Math.round(window.scrollY);
    };
    go();
    setTimeout(go, 80);
    setTimeout(go, 250);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(go);
  }

  const recentStrip = el('div', { class: 'recent' });
  function renderRecent() {
    const rec = (ui.get().recent || [])
      .map((id) => snap.speciesById.get(id))
      .filter(Boolean)
      .slice(0, 12);
    clear(recentStrip);
    recentStrip.hidden = rec.length === 0;
    if (!rec.length) return;
    recentStrip.append(el('span', { class: 'recent__label' }, 'Recent'));
    const style = settings.get().spriteStyle;
    for (const sp of rec) {
      recentStrip.append(
        el(
          'a',
          { class: 'recent__item', href: `#/pokemon/${sp.id}`, title: sp.name.replace(/-/g, ' ') },
          Sprite(sp.id, { style, alt: sp.name, size: 44 }),
        ),
      );
    }
  }
  renderRecent();

  const tools = el(
    'div',
    { class: `feed-tools${ui.get().toolsOpen ? ' is-open' : ''}` },
    filterBar,
    el(
      'div',
      { class: 'feed-sort' },
      el('span', { class: 'feed-sort__label' }, 'Sort'),
      sortSelect,
      dirBtn,
    ),
    el('div', { class: 'feed-sort feed-filters' }, typeSelect, genSelect, colorSelect, randomBtn),
    el('div', { class: 'feed-sort feed-filters' }, abilitySelect, eggGroupSelect, minBstField),
    el('div', { class: 'feed-sort feed-filters' }, fullyEvolvedCheck),
  );
  const toolsToggle = el(
    'button',
    {
      class: 'feed-tools__toggle',
      type: 'button',
      'aria-expanded': String(!!ui.get().toolsOpen),
      onclick: () => {
        const open = tools.classList.toggle('is-open');
        toolsToggle.setAttribute('aria-expanded', String(open));
        ui.set({ toolsOpen: open });
      },
    },
    'Filters & sort',
    el('span', { class: 'feed-tools__chev', 'aria-hidden': 'true' }, '▾'),
  );
  function syncToolsBadge() {
    const n =
      (filterKey !== 'all' ? 1 : 0) +
      (filterType ? 1 : 0) +
      (filterGen ? 1 : 0) +
      (filterAbility ? 1 : 0) +
      (filterEggGroup ? 1 : 0) +
      (filterColor ? 1 : 0) +
      (filterMinBst ? 1 : 0) +
      (filterFullyEvolved ? 1 : 0);
    toolsToggle.dataset.active = n ? String(n) : '';
  }
  syncToolsBadge();

  // Easter egg: a flurry of taps on the game title opens the hidden game.
  // (The Konami code does the same with a keyboard.)
  const feedId = el('div', { class: 'feed-head__id' }, title, gameLabel);
  onTapCode(feedId, () => {
    if ((location.hash || '') !== '#/whos-that') location.hash = '#/whos-that';
  });

  view.append(
    el(
      'header',
      { class: 'feed-head' },
      el('div', { class: 'feed-head__top' }, feedId, ring.node),
      el(
        'div',
        { class: 'field-search' },
        el('span', { class: 'field-search__icon', html: SEARCH_ICON, 'aria-hidden': 'true' }),
        search,
      ),
      toolsToggle,
      tools,
    ),
    recentStrip,
    grid,
  );

  function counts() {
    let caught = 0;
    let favorite = 0;
    let legendary = 0;
    let notes = 0;
    for (const { species } of rows) {
      const e = entry(species.id);
      if (e.caught) caught++;
      if (e.favorite) favorite++;
      if (species.isLegendary || species.isMythical) legendary++;
      if (e.note && e.note.trim()) notes++;
    }
    return { all: rows.length, caught, missing: rows.length - caught, favorite, legendary, notes };
  }

  function refreshMeta() {
    ring.update(stats(ids));
    const c = counts();
    chipEls.forEach(({ count }, i) => {
      count.textContent = c[FILTERS[i].key];
    });
  }

  function paintGrid() {
    const st = settings.get();
    const gen = snap.versionGroupByKey.get(st.versionGroup)?.generation ?? 9;
    const boxed = st.spriteStyle === 'game' && gen <= 2;
    const filterTest = FILTERS.find((f) => f.key === filterKey).test;
    const abilityMon = filterAbility
      ? new Set(snap.abilityById.get(filterAbility)?.pokemon || [])
      : null;

    let list = rows.filter(({ species, number }) => {
      if (
        query &&
        !species.name.includes(query) &&
        String(number) !== query &&
        String(species.id) !== query
      ) {
        return false;
      }
      if (filterGen && species.generation !== filterGen) return false;
      if (filterType && !typesForGen(species, gen).includes(filterType)) return false;
      if (abilityMon && !abilityMon.has(species.id)) return false;
      if (filterEggGroup && !(species.eggGroups || []).includes(filterEggGroup)) return false;
      if (filterColor && species.color !== filterColor) return false;
      if (filterMinBst && (species.bst || 0) < filterMinBst) return false;
      if (filterFullyEvolved && !isFullyEvolved(species)) return false;
      return filterTest(entry(species.id), species);
    });

    const dir = sortDesc ? -1 : 1;
    list = list
      .map((row) => ({ row, v: sortValue(row, sortKey) }))
      .sort((a, b) => {
        if (a.v < b.v) return -dir;
        if (a.v > b.v) return dir;
        return (a.row.number ?? a.row.species.id) - (b.row.number ?? b.row.species.id);
      })
      .map((x) => x.row);
    lastList = list;

    const frag = document.createDocumentFragment();
    list.forEach(({ species, number }, i) => {
      const card = Card(species, number, {
        spriteStyle: st.spriteStyle,
        versionGroup: st.versionGroup,
        gen,
        boxed,
        metric: metricLabel(sortKey, species),
      });
      if (i < 30) card.style.setProperty('--i', String(i));
      frag.append(card);
    });

    clear(grid);
    grid.append(
      list.length === 0
        ? el('p', { class: 'grid__empty' }, 'Nothing matches those filters.')
        : frag,
    );
  }

  function rebuild() {
    const st = settings.get();
    const dex = resolvePokedex(snap, st);
    rows = dexRows(snap, dex);
    ids = rows.map((r) => r.species.id);

    title.textContent = prettify(dex.name || dex.key);
    gameLabel.textContent = st.versionGroup === 'all' ? 'All games' : prettify(st.versionGroup);
    refreshMeta();
    paintGrid();
  }

  let mountedVG = settings.get().versionGroup;
  rebuild();
  // Return to where the user last scrolled (e.g. after viewing a Pokémon).
  requestAnimationFrame(restoreScroll);

  const offSettings = settings.subscribe((s) => {
    if (s.versionGroup !== mountedVG) {
      mountedVG = s.versionGroup;
      ui.set({ feedScroll: 0 });
    }
    rebuild();
  });
  const offSelection = selection.subscribe(() => {
    refreshMeta();
    if (filterKey !== 'all' && filterKey !== 'legendary') paintGrid();
    else if (sortKey === 'caught') paintGrid();
  });
  onTeardown(view, () => {
    ui.set({ feedScroll: window.scrollY });
    offSettings();
    offSelection();
  });

  return view;
}
